import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Camera, IndianRupee, Plus, Save, ScanLine, Send, Trash2, X, Info } from "lucide-react";
import { api, apiError, complianceErrors } from "@/lib/api";
import { useAuth, useHasModule } from "@/context/AuthContext";
import CustomerPicker from "@/components/CustomerPicker";
import { inr, num, today, addDays, titleCase, PAYMENT_MODES } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// zxing is heavy and most invoices are typed, not scanned, so the camera
// scanner is only fetched when someone actually opens it.
const CodeScanner = lazy(() => import("@/components/CodeScanner"));

/**
 * Where this line's rate came from — a price list, or the person typing.
 * Silent for an ordinary item at its ordinary price: a note on every line
 * would be noise, and the only two things worth saying are "this is the
 * dealer rate" and "someone has overridden it".
 */
/**
 * The schedule itself. Everything else on this screen is the invoice that will
 * come out of it, which is why it reuses the invoice editor unchanged.
 */
function RecurringSchedule({ form, setForm, options }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-5 space-y-4"
      data-testid="recurring-schedule">
      <div className="space-y-1.5">
        <Label className="text-xs">Schedule name</Label>
        <Input value={form.schedule_name} data-testid="recurring-name"
          placeholder="Acme AMC — monthly"
          onChange={(e) => setForm((f) => ({ ...f, schedule_name: e.target.value }))} />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label className="text-xs">Repeats</Label>
          <Select value={form.frequency}
            onValueChange={(v) => setForm((f) => ({ ...f, frequency: v }))}>
            <SelectTrigger data-testid="recurring-frequency"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(options.frequencies || []).map((x) => (
                <SelectItem key={x.code} value={x.code}>{x.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Stop after (invoices)</Label>
          <Input className="font-mono text-right" value={form.max_occurrences}
            data-testid="recurring-max" placeholder="No limit"
            onChange={(e) => setForm((f) => ({ ...f, max_occurrences: e.target.value }))} />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">On the day</Label>
        <Select value={form.action}
          onValueChange={(v) => setForm((f) => ({ ...f, action: v }))}>
          <SelectTrigger data-testid="recurring-action"><SelectValue /></SelectTrigger>
          <SelectContent>
            {(options.actions || []).map((x) => (
              <SelectItem key={x.code} value={x.code}>{x.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {form.action === "ISSUE_AND_EMAIL" && (
          <p className="text-[11px] text-slate-500">
            The customer needs an email address on file, or this will be refused.
          </p>
        )}
      </div>
      <p className="text-[11px] text-slate-500">
        Generated overnight. A schedule that falls behind catches up, and each
        invoice is dated the day it was due — not the day it was generated.
      </p>
    </div>
  );
}

/**
 * What a delivery challan needs that an invoice does not: why the goods are
 * moving, and how. The purpose is the important one — it decides whether the
 * stock actually leaves, because goods sent for job work are still yours.
 */
function ChallanDetails({ form, setForm, purposes }) {
  const chosen = purposes.find((p) => p.code === form.purpose);
  const wantsReturn = ["JOB_WORK", "APPROVAL"].includes(form.purpose);
  return (
    <div className="rounded-md border border-slate-200 bg-white p-5 space-y-4"
      data-testid="challan-details">
      <div className="space-y-1.5">
        <Label className="text-xs">Purpose (Rule 55)</Label>
        <Select value={form.purpose}
          onValueChange={(v) => setForm((f) => ({ ...f, purpose: v }))}>
          <SelectTrigger data-testid="challan-purpose"><SelectValue /></SelectTrigger>
          <SelectContent>
            {purposes.map((p) => <SelectItem key={p.code} value={p.code}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <p className="text-[11px] text-slate-500">
          {chosen?.reduces_stock
            ? "Stock comes out when this challan is issued, and the invoice raised from it will not take it out again."
            : "The goods are still yours, so stock is untouched until an invoice is raised."}
        </p>
      </div>

      {wantsReturn && (
        <div className="space-y-1.5">
          <Label className="text-xs">Expected back by</Label>
          <Input type="date" value={form.expected_return_date} data-testid="challan-return-date"
            onChange={(e) => setForm((f) => ({ ...f, expected_return_date: e.target.value }))} />
        </div>
      )}

      <div className="space-y-3 border-t border-slate-100 pt-3">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          Transport
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Vehicle no.</Label>
            <Input className="font-mono uppercase" value={form.vehicle_number}
              data-testid="challan-vehicle" placeholder="MH12AB1234"
              onChange={(e) => setForm((f) => ({ ...f, vehicle_number: e.target.value.toUpperCase() }))} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">LR / RR no.</Label>
            <Input className="font-mono" value={form.lr_number} data-testid="challan-lr"
              onChange={(e) => setForm((f) => ({ ...f, lr_number: e.target.value }))} />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Transporter</Label>
          <Input value={form.transporter_name} data-testid="challan-transporter"
            onChange={(e) => setForm((f) => ({ ...f, transporter_name: e.target.value }))} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Deliver to</Label>
          <Input value={form.delivery_address} data-testid="challan-delivery"
            placeholder="Leave blank to use the customer's shipping address"
            onChange={(e) => setForm((f) => ({ ...f, delivery_address: e.target.value }))} />
        </div>
      </div>
    </div>
  );
}

/**
 * TCS and TDS. Off by default and collapsed, because most invoices carry
 * neither and a permanently-visible pair of tax sections is clutter for the
 * businesses that never touch them.
 */
function TaxAtSource({ form, setForm, sections }) {
  const on = !!(form.tcs_section || form.tds_section);
  const [open, setOpen] = useState(on);

  const pick = (kind) => (code) => {
    const found = (sections[kind] || []).find((x) => x.code === code);
    setForm((f) => ({
      ...f,
      [`${kind}_section`]: code === "NONE" ? "" : code,
      [`${kind}_rate`]: code === "NONE" ? 0 : (found?.rate ?? f[`${kind}_rate`]),
    }));
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid="tax-at-source-open"
        className="w-full rounded-md border border-dashed border-slate-300 px-4 py-2.5 text-xs text-slate-500 hover:border-slate-400 hover:text-slate-700 transition-colors duration-150">
        + Add TDS or TCS
      </button>
    );
  }

  return (
    <div className="rounded-md border border-slate-200 bg-white p-5 space-y-4"
      data-testid="tax-at-source">
      <div className="flex items-center justify-between">
        <Label className="text-xs">Tax at source</Label>
        <button className="text-[11px] text-slate-400 hover:text-slate-700 transition-colors duration-150"
          onClick={() => { setOpen(false); setForm((f) => ({ ...f,
            tcs_section: "", tcs_rate: 0, tds_section: "", tds_rate: 0 })); }}
          data-testid="tax-at-source-clear">Clear</button>
      </div>

      <div className="space-y-2">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          TCS — you collect it
        </div>
        <div className="flex gap-2">
          <Select value={form.tcs_section || "NONE"} onValueChange={pick("tcs")}>
            <SelectTrigger className="h-9 flex-1 text-xs" data-testid="tcs-section">
              <SelectValue placeholder="No TCS" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="NONE">No TCS</SelectItem>
              {(sections.tcs || []).map((x) => (
                <SelectItem key={x.code} value={x.code}>{x.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input className="h-9 w-20 text-right font-mono text-xs" data-testid="tcs-rate"
            value={form.tcs_rate}
            onChange={(e) => setForm((f) => ({ ...f, tcs_rate: e.target.value }))} />
        </div>
        {!!form.tcs_section && (
          <Select value={form.tcs_base}
            onValueChange={(v) => setForm((f) => ({ ...f, tcs_base: v }))}>
            <SelectTrigger className="h-8 text-[11px]" data-testid="tcs-base"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="TOTAL">On the total including GST</SelectItem>
              <SelectItem value="TAXABLE">On the taxable value only</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="space-y-2 border-t border-slate-100 pt-3">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          TDS — your customer deducts it
        </div>
        <div className="flex gap-2">
          <Select value={form.tds_section || "NONE"} onValueChange={pick("tds")}>
            <SelectTrigger className="h-9 flex-1 text-xs" data-testid="tds-section">
              <SelectValue placeholder="No TDS" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="NONE">No TDS</SelectItem>
              {(sections.tds || []).map((x) => (
                <SelectItem key={x.code} value={x.code}>{x.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input className="h-9 w-20 text-right font-mono text-xs" data-testid="tds-rate"
            value={form.tds_rate}
            onChange={(e) => setForm((f) => ({ ...f, tds_rate: e.target.value }))} />
        </div>
        {!!form.tds_section && (
          <Select value={form.tds_base}
            onValueChange={(v) => setForm((f) => ({ ...f, tds_base: v }))}>
            <SelectTrigger className="h-8 text-[11px]" data-testid="tds-base"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="TAXABLE">On the value excluding GST</SelectItem>
              <SelectItem value="TOTAL">On the total including GST</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      <p className="text-[11px] text-slate-500">
        Rates are suggestions and change with each Finance Act — type over them if
        your CA says otherwise. Neither affects your GST returns.
      </p>
    </div>
  );
}

function RateNote({ line }) {
  if (!line.item_id || line.auto_rate === null) return null;
  const overridden = Number(line.rate) !== Number(line.auto_rate);
  if (overridden) {
    return (
      <div className="mt-0.5 text-right text-[10px] text-amber-700" data-testid="rate-note-override">
        Overridden · was {line.auto_rate}
      </div>
    );
  }
  if (line.rate_source === "DEFAULT") return null;
  return (
    <div className="mt-0.5 text-right text-[10px] text-blue-700" data-testid="rate-note-list">
      {line.rate_list_name}
    </div>
  );
}

const blankLine = () => ({
  item_id: null, item_name: "", description: "", hsn_sac_code: "", unit_symbol: "NOS",
  uqc_code: "NOS-NUMBERS", quantity: 1, rate: 0, price_includes_tax: false,
  discount_type: "PERCENT", discount_value: 0, tax_rate_percent: 18, cess_rate: 0,
  cess_per_unit: 0, tax_treatment: "TAXABLE",
  // The rate we filled in for them. If l.rate still equals it, nobody has
  // typed over it, so re-pricing on a customer change is safe.
  auto_rate: null, rate_source: null, rate_list_name: null,
});

/**
 * One editor, three documents. A quotation is priced by exactly the same
 * engine as the invoice it will become, so giving it its own editor would
 * have meant maintaining the same discount, tax and rounding behaviour twice
 * and watching them drift.
 */
const MODES = {
  INVOICE: {
    noun: "Invoice", newTitle: "New Invoice", issueLabel: "Issue invoice",
    api: "/documents", back: "/invoices",
    dateLabel: "Invoice date", secondDateLabel: "Due date",
  },
  QUOTATION: {
    noun: "Quotation", newTitle: "New Quotation", issueLabel: "Send quotation",
    api: "/quotations", back: "/quotations",
    dateLabel: "Quotation date", secondDateLabel: "Valid until",
  },
  PROFORMA: {
    noun: "Proforma", newTitle: "New Proforma Invoice", issueLabel: "Send proforma",
    api: "/quotations", back: "/quotations",
    dateLabel: "Proforma date", secondDateLabel: "Valid until",
  },
  CHALLAN: {
    noun: "Challan", newTitle: "New Delivery Challan", issueLabel: "Issue challan",
    api: "/challans", back: "/challans",
    dateLabel: "Challan date", secondDateLabel: null,
  },
  RECURRING: {
    noun: "Schedule", newTitle: "New Recurring Invoice", issueLabel: "Save schedule",
    api: "/recurring", back: "/invoices?tab=recurring",
    dateLabel: "First invoice on", secondDateLabel: "Stop on (optional)",
  },
};

export default function InvoiceEditor({ mode = "INVOICE" }) {
  const cfg = MODES[mode];
  const isChallan = mode === "CHALLAN";
  const isRecurring = mode === "RECURRING";
  // Everything that is not a tax invoice: priced the same way, saved elsewhere.
  const isQuote = mode !== "INVOICE";
  const { id } = useParams();
  const navigate = useNavigate();
  const { organization } = useAuth();
  const [params] = useSearchParams();
  // Composition and unregistered suppliers cannot collect GST, so the editor
  // hides the tax inputs and columns instead of showing them stuck at zero.
  const showGst = organization?.registration_type === "REGULAR" && !!organization?.gstin;
  const [parties, setParties] = useState([]);
  const [items, setItems] = useState([]);
  const [states, setStates] = useState([]);
  const [preview, setPreview] = useState(null);
  const [errors, setErrors] = useState([]);
  const [busy, setBusy] = useState(false);
  const [existing, setExisting] = useState(null);
  const [rates, setRates] = useState({});
  const [sections, setSections] = useState({ tds: [], tcs: [] });
  const [purposes, setPurposes] = useState([]);
  const [recurOptions, setRecurOptions] = useState({ frequencies: [], actions: [] });
  const [form, setForm] = useState({
    party_id: params.get("party_id") || "",
    document_date: today(),
    due_date: addDays(today(), 15),
    place_of_supply_code: "",
    document_discount_type: "AMOUNT",
    document_discount_value: 0,
    is_reverse_charge: false,
    schedule_name: "", frequency: "MONTHLY", interval: 1, max_occurrences: "",
    action: "DRAFT",
    purpose: "SUPPLY", vehicle_number: "", transporter_name: "", lr_number: "",
    expected_return_date: "", delivery_address: "",
    tcs_section: "", tcs_rate: 0, tcs_base: "TOTAL",
    tds_section: "", tds_rate: 0, tds_base: "TAXABLE",
    notes: "",
    terms_and_conditions: "",
    lines: [blankLine()],
  });
  const lastRowRef = useRef(null);

  useEffect(() => {
    api.get("/parties", { params: { kind: "CUSTOMER" } }).then(({ data }) => setParties(data));
    api.get("/items").then(({ data }) => setItems(data));
    api.get("/masters/states").then(({ data }) => setStates(data));
    api.get("/masters/tax-sections").then(({ data }) => setSections(data)).catch(() => {});
    api.get("/challans/purposes").then(({ data }) => setPurposes(data)).catch(() => {});
    api.get("/recurring/options").then(({ data }) => setRecurOptions(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!id) return;
    api.get(`${cfg.api}/${id}`).then(({ data }) => {
      setExisting(data);
      setForm({
        party_id: data.party_id,
        document_date: isRecurring ? data.start_date
                      : isChallan ? data.challan_date
                      : isQuote ? data.quote_date : data.document_date,
        due_date: isRecurring ? (data.end_date || "")
                : isChallan ? "" : isQuote ? data.valid_until : data.due_date,
        schedule_name: data.name || "",
        frequency: data.frequency || "MONTHLY",
        interval: data.interval || 1,
        max_occurrences: data.max_occurrences || "",
        action: data.action || "DRAFT",
        purpose: data.purpose || "SUPPLY",
        vehicle_number: data.vehicle_number || "",
        transporter_name: data.transporter_name || "",
        lr_number: data.lr_number || "",
        expected_return_date: data.expected_return_date || "",
        delivery_address: data.shipping_address || "",
        place_of_supply_code: data.place_of_supply_code,
        document_discount_type: data.document_discount_type || "AMOUNT",
        document_discount_value: data.document_discount_value || 0,
        is_reverse_charge: !!data.is_reverse_charge,
        tcs_section: data.totals?.tcs_section || "",
        tcs_rate: data.totals?.tcs_rate || 0,
        tcs_base: data.totals?.tcs_base_kind || "TOTAL",
        tds_section: data.totals?.tds_section || "",
        tds_rate: data.totals?.tds_rate || 0,
        tds_base: data.totals?.tds_base_kind || "TAXABLE",
        notes: data.notes || "",
        terms_and_conditions: data.terms_and_conditions || "",
        lines: data.lines.map((l) => ({ ...blankLine(), ...l })),
      });
    });
  }, [id]);

  const party = useMemo(() => parties.find((p) => p.id === form.party_id), [parties, form.party_id]);

  useEffect(() => {
    if (!party) return;
    setForm((f) => ({
      ...f,
      place_of_supply_code: f.place_of_supply_code || party.shipping_state_code || party.state_code,
      due_date: id ? f.due_date : addDays(f.document_date, party.credit_period_days || 15),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [party?.id]);

  const runPreview = useCallback(async (payload) => {
    try {
      const { data } = await api.post("/documents/preview-totals", {
        party_id: payload.party_id || null,
        place_of_supply_code: payload.place_of_supply_code || null,
        document_discount_type: payload.document_discount_type,
        document_discount_value: Number(payload.document_discount_value) || 0,
        tcs_section: payload.tcs_section || null,
        tcs_rate: Number(payload.tcs_rate) || 0,
        tcs_base: payload.tcs_base,
        tds_section: payload.tds_section || null,
        tds_rate: Number(payload.tds_rate) || 0,
        tds_base: payload.tds_base,
        lines: payload.lines.map((l) => ({
          ...l,
          quantity: Number(l.quantity) || 0,
          rate: Number(l.rate) || 0,
          discount_value: Number(l.discount_value) || 0,
          tax_rate_percent: Number(l.tax_rate_percent) || 0,
        })),
      });
      setPreview(data);
    } catch (e) {
      /* preview is advisory */
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => runPreview(form), 200);
    return () => clearTimeout(t);
  }, [form, runPreview]);

  // What this customer pays, for every item already on the invoice plus the
  // whole catalogue the picker offers. One call, refreshed when the customer
  // changes — the rate has to follow the customer, not the item.
  useEffect(() => {
    const ids = items.map((i) => i.id);
    if (!ids.length) return;
    let cancelled = false;
    api.post("/price-lists/resolve", { party_id: form.party_id || null, item_ids: ids })
      .then(({ data }) => {
        if (cancelled) return;
        setRates(data);
        // Re-price only the lines still sitting at the rate we put there.
        setForm((f) => ({
          ...f,
          lines: f.lines.map((l) => {
            const r = l.item_id && data[l.item_id];
            if (!r) return l;
            const untouched = l.auto_rate !== null && Number(l.rate) === Number(l.auto_rate);
            if (!untouched) return l;
            return { ...l, rate: r.rate, auto_rate: r.rate,
                     rate_source: r.source, rate_list_name: r.price_list_name };
          }),
        }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.party_id, items]);

  const setLine = (idx, patch) =>
    setForm((f) => ({ ...f, lines: f.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)) }));

  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, blankLine()] }));
  const removeLine = (idx) =>
    setForm((f) => ({ ...f, lines: f.lines.length > 1 ? f.lines.filter((_, i) => i !== idx) : f.lines }));

  /**
   * The line a given item produces. Pure, so both the dropdown and the
   * scanner build a line exactly the same way.
   */
  const lineFromItem = useCallback((it) => {
    const priced = rates[it.id];
    return {
      item_id: it.id, item_name: it.name, hsn_sac_code: it.hsn_sac_code,
      unit_symbol: it.unit_symbol, uqc_code: it.uqc_code,
      rate: priced ? priced.rate : it.sale_price,
      auto_rate: priced ? priced.rate : it.sale_price,
      rate_source: priced ? priced.source : "DEFAULT",
      rate_list_name: priced ? priced.price_list_name : null,
      tax_rate_percent: it.tax_rate_percent, tax_treatment: it.tax_treatment,
      price_includes_tax: priced ? priced.price_includes_tax : it.sale_price_includes_tax,
    };
  }, [rates]);

  const applyItem = (idx, it) => { if (it) setLine(idx, lineFromItem(it)); };

  const pickItem = (idx, itemId) => applyItem(idx, items.find((i) => i.id === itemId));

  // Already out with the customer: saving edits it rather than re-sending it.
  const readOnlyIssued = existing?.status === "ISSUED"
    || (isQuote && existing?.status && existing.status !== "DRAFT");

  // ------------------------------------------------------- money at the till
  // Retail does not issue an invoice and then go hunting for a payment
  // screen: the customer is standing there. Collecting rides along with the
  // issue, as one call, so a counter sale is one action.
  const hasPayments = useHasModule("payments");
  const hasBanking = useHasModule("banking");
  const canCollect = !isQuote && !isChallan && !isRecurring && !readOnlyIssued && hasPayments;

  const [collect, setCollect] = useState(false);
  const [collectForm, setCollectForm] = useState({
    amount: "", payment_mode: "CASH", account_id: "", reference_number: "",
  });
  const [accounts, setAccounts] = useState([]);
  // True once someone types an amount, after which the running total stops
  // overwriting what they typed.
  const amountTouched = useRef(false);

  useEffect(() => {
    if (!canCollect || !hasBanking) return;
    api.get("/banking/accounts").then(({ data }) => {
      setAccounts(data);
      setCollectForm((f) => (f.account_id ? f : {
        ...f, account_id: data.find((a) => a.is_default)?.id || data[0]?.id || "",
      }));
    }).catch(() => setAccounts([]));
  }, [canCollect, hasBanking]);

  // Keep the amount on the full total while nobody has overridden it, so the
  // common case — paid in full — needs no typing at all.
  const grandTotal = preview?.totals?.net_receivable ?? preview?.totals?.grand_total;
  useEffect(() => {
    if (!collect || amountTouched.current || grandTotal == null) return;
    setCollectForm((f) => ({ ...f, amount: String(grandTotal) }));
  }, [collect, grandTotal]);

  // ------------------------------------------------------------- scanning
  const [scanCode, setScanCode] = useState("");
  const [scanBusy, setScanBusy] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const scanRef = useRef(null);

  // Same squashing the server does, so a code read with a stray space or a
  // hyphen still matches without a round trip.
  const squash = (raw) => String(raw || "").replace(/[\s\-_]+/g, "").toUpperCase();

  const localMatch = useCallback((raw) => {
    const c = squash(raw);
    if (!c) return null;
    return items.find((i) => squash(i.scan_code) === c)
      || items.find((i) => squash(i.barcode) === c)
      || items.find((i) => squash(i.item_code) === c)
      || null;
  }, [items]);

  const addScanned = useCallback((it) => {
    // A scanned item can be outside the page of items this editor loaded, so
    // it has to join the list or the dropdown would show a blank name.
    setItems((prev) => (prev.some((i) => i.id === it.id) ? prev : [...prev, it]));
    setForm((f) => {
      const at = f.lines.findIndex((l) => l.item_id === it.id);
      // Scanning the same thing twice means two of it, not a second line —
      // which is how a counter actually rings up three identical bottles.
      if (at >= 0) {
        return { ...f, lines: f.lines.map((l, i) =>
          (i === at ? { ...l, quantity: (Number(l.quantity) || 0) + 1 } : l)) };
      }
      const patch = lineFromItem(it);
      const blankAt = f.lines.findIndex((l) => !l.item_id && !l.item_name);
      if (blankAt >= 0) {
        return { ...f, lines: f.lines.map((l, i) =>
          (i === blankAt ? { ...l, ...patch } : l)) };
      }
      return { ...f, lines: [...f.lines, { ...blankLine(), ...patch }] };
    });
  }, [lineFromItem]);

  const submitCode = useCallback(async (raw) => {
    const code = String(raw || "").trim();
    if (!code) return;
    const local = localMatch(code);
    if (local) {
      addScanned(local);
      setScanCode("");
      toast.success(`Added ${local.name}`);
      return;
    }
    setScanBusy(true);
    try {
      const { data } = await api.get("/items/lookup", { params: { code } });
      addScanned(data);
      toast.success(`Added ${data.name}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      // Always clear: a failed scan left in the box would get the next one
      // appended to it, and the second scan would fail too.
      setScanCode("");
      setScanBusy(false);
      scanRef.current?.focus();
    }
  }, [localMatch, addScanned]);

  const save = useCallback(
    async (issue) => {
      if (!form.party_id) {
        toast.error("Select a customer first");
        return;
      }
      if (isRecurring && !form.schedule_name.trim()) {
        toast.error("Give the schedule a name");
        return;
      }
      setBusy(true);
      setErrors([]);
      const payload = {
        ...(isRecurring
          ? { name: form.schedule_name, frequency: form.frequency,
              interval: Number(form.interval) || 1,
              start_date: form.document_date,
              end_date: form.due_date || null,
              max_occurrences: Number(form.max_occurrences) || null,
              action: form.action }
          : isChallan
          ? { challan_date: form.document_date, purpose: form.purpose,
              vehicle_number: form.vehicle_number || null,
              transporter_name: form.transporter_name || null,
              lr_number: form.lr_number || null,
              expected_return_date: form.expected_return_date || null,
              delivery_address: form.delivery_address || null }
          : isQuote
          ? { kind: mode, quote_date: form.document_date, valid_until: form.due_date }
          : { document_type: "SALES_INVOICE", document_date: form.document_date,
              due_date: form.due_date }),
        party_id: form.party_id,
        place_of_supply_code: form.place_of_supply_code || null,
        is_reverse_charge: form.is_reverse_charge,
        document_discount_type: form.document_discount_type,
        document_discount_value: Number(form.document_discount_value) || 0,
        tcs_section: form.tcs_section || null,
        tcs_rate: Number(form.tcs_rate) || 0,
        tcs_base: form.tcs_base,
        tds_section: form.tds_section || null,
        tds_rate: Number(form.tds_rate) || 0,
        tds_base: form.tds_base,
        notes: form.notes,
        terms_and_conditions: form.terms_and_conditions || null,
        lines: form.lines
          .filter((l) => l.item_name)
          .map((l) => ({
            ...l,
            quantity: Number(l.quantity) || 0,
            rate: Number(l.rate) || 0,
            discount_value: Number(l.discount_value) || 0,
            tax_rate_percent: Number(l.tax_rate_percent) || 0,
          })),
        issue,
      };
      if (issue && canCollect && collect) {
        const amount = Number(collectForm.amount);
        if (!(amount > 0)) {
          toast.error("Enter how much the customer paid, or switch collection off");
          setBusy(false);
          return;
        }
        payload.collect = {
          amount,
          payment_mode: collectForm.payment_mode,
          account_id: collectForm.account_id || null,
          reference_number: collectForm.reference_number || null,
        };
      }
      try {
        const { data } = id
          ? await api.patch(`${cfg.api}/${id}`, payload)
          : await api.post(cfg.api, payload);
        const number = data.document_number || data.number;
        toast.success(issue ? `${cfg.noun} ${number || ""} ${isQuote ? "sent" : "issued"}`.trim()
                            : "Draft saved");
        navigate(isQuote ? `${cfg.back}` : `/invoices/${data.id}`);
      } catch (e) {
        const details = complianceErrors(e);
        setErrors(details);
        toast.error(details.length ? `This ${cfg.noun.toLowerCase()} is not GST compliant yet`
                                   : apiError(e));
      } finally {
        setBusy(false);
      }
    },
    [form, id, navigate, cfg, isQuote, mode, canCollect, collect, collectForm]
  );

  useEffect(() => {
    const handler = (e) => {
      if (e.altKey && e.key.toLowerCase() === "n") {
        e.preventDefault();
        addLine();
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        save(false);
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "enter") {
        e.preventDefault();
        save(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [save]);

  const t = preview?.totals || {};
  const isIntra = preview?.supply_type === "INTRA_STATE";

  return (
    <div className="space-y-5" data-testid="invoice-editor-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight sm:text-3xl">
            {id ? `Edit ${existing?.document_number || existing?.number || "draft"}` : cfg.newTitle}
          </h1>
          <p className="mt-1 hidden items-center gap-2 text-sm text-slate-500 sm:flex">
            <kbd className="rounded border border-slate-300 bg-white px-1.5 py-0.5 font-mono text-[10px]">Alt+N</kbd> add line
            <kbd className="rounded border border-slate-300 bg-white px-1.5 py-0.5 font-mono text-[10px]">Ctrl+S</kbd> save draft
            <kbd className="rounded border border-slate-300 bg-white px-1.5 py-0.5 font-mono text-[10px]">Ctrl+Enter</kbd> issue
          </p>
        </div>
        <div className="hidden gap-2 sm:flex">
          <Button variant="outline" size="sm" data-testid="cancel-invoice" onClick={() => navigate(-1)} className="gap-1.5">
            <X size={15} /> Cancel
          </Button>
          {!readOnlyIssued && !isRecurring && (
            <Button variant="outline" size="sm" disabled={busy} data-testid="save-draft-btn" onClick={() => save(false)} className="gap-1.5">
              <Save size={15} /> Save draft
            </Button>
          )}
          <Button size="sm" disabled={busy} data-testid="issue-invoice-btn" onClick={() => save(!readOnlyIssued)} className="gap-1.5 bg-blue-600 hover:bg-blue-700">
            <Send size={15} /> {readOnlyIssued ? "Save changes"
              : collect && canCollect ? "Issue & collect" : cfg.issueLabel}
          </Button>
        </div>
      </div>

      {readOnlyIssued && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800 flex gap-2" data-testid="issued-edit-warning">
          <Info size={15} className="shrink-0 mt-0.5" />
          This invoice is already issued and may have been sent to the customer. Editing may cause a
          mismatch — consider a credit note instead. Edits are versioned and audit-logged.
        </div>
      )}

      {!!errors.length && (
        <div className="rounded-md border border-rose-200 bg-rose-50 p-4" data-testid="compliance-errors">
          <div className="text-xs font-semibold uppercase tracking-widest text-rose-800">
            GST compliance check failed
          </div>
          <ul className="mt-2 space-y-1 text-xs text-rose-700 list-disc pl-4">
            {errors.map((e, i) => (
              <li key={i}>
                <span className="font-mono">{e.field}</span> — {e.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-12">
        <div className="min-w-0 lg:col-span-8 space-y-5">
          <div className="rounded-md border border-slate-200 bg-white p-5 grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Customer</Label>
              <CustomerPicker value={form.party_id} disabled={readOnlyIssued}
                onChange={(id) => {
                  setForm((f) => ({ ...f, party_id: id }));
                  // A customer created here is not in the list the editor
                  // loaded, and the panel below reads from that list.
                  api.get(`/parties/${id}`)
                    .then(({ data }) => setParties((prev) =>
                      prev.some((x) => x.id === data.id) ? prev : [...prev, data]))
                    .catch(() => {});
                }} />
              {party && (
                <div className="text-[11px] text-slate-500 leading-relaxed">
                  {party.billing_address} · {party.state_name} ({party.state_code}) · credit {party.credit_period_days} days
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{cfg.dateLabel}</Label>
              <Input type="date" data-testid="invoice-date" value={form.document_date}
                onChange={(e) => setForm((f) => ({ ...f, document_date: e.target.value }))} />
            </div>
            <div className={`space-y-1.5 ${cfg.secondDateLabel ? "" : "hidden"}`}>
              <Label className="text-xs">{cfg.secondDateLabel || "Due date"}</Label>
              <Input type="date" data-testid="invoice-due-date" value={form.due_date}
                onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Place of supply</Label>
              <Select value={form.place_of_supply_code}
                onValueChange={(v) => setForm((f) => ({ ...f, place_of_supply_code: v }))}>
                <SelectTrigger data-testid="invoice-pos-select"><SelectValue placeholder="Select state" /></SelectTrigger>
                <SelectContent>
                  {states.map((s) => (
                    <SelectItem key={s.code} value={s.code}>{s.name} ({s.code})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[11px] text-slate-500">
                Defaulted from the shipping state. Determines CGST+SGST vs IGST and is frozen at issue.
              </div>
            </div>
          </div>

          <div className="rounded-md border border-slate-200 bg-white">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <h2 className="text-sm font-semibold uppercase tracking-widest text-slate-600">Line items</h2>
              <Button size="sm" variant="outline" data-testid="add-line-btn" onClick={addLine} className="h-9 gap-1 text-xs sm:h-7">
                <Plus size={13} /> Add line
              </Button>
            </div>
            {!readOnlyIssued && (
              /* A scanner gun is just a keyboard — it types the code and presses
                 Enter — so a focused input is the whole integration. The camera
                 button is for phones and tablets that have no gun. */
              <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50/70 px-4 py-2.5">
                <ScanLine size={15} className="shrink-0 text-slate-400" />
                <form className="min-w-[160px] flex-1"
                  onSubmit={(e) => { e.preventDefault(); submitCode(scanCode); }}>
                  <Input ref={scanRef} data-testid="scan-input" value={scanCode}
                    disabled={scanBusy} autoComplete="off"
                    onChange={(e) => setScanCode(e.target.value)}
                    className="h-8 font-mono text-xs"
                    placeholder="Scan or type an item code, then press Enter" />
                </form>
                <Button type="button" size="sm" variant="outline" data-testid="scan-camera-btn"
                  onClick={() => setScannerOpen(true)} className="h-8 shrink-0 gap-1.5 text-xs">
                  <Camera size={13} /> Camera
                </Button>
              </div>
            )}
            {/* Table from lg up; below that each line becomes a card so no
                field is hidden behind a horizontal scroll on a phone. */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full text-xs" data-testid="editor-lines-table">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-2 py-2 text-left font-semibold w-[26%]">Item</th>
                    {showGst && <th className="px-2 py-2 text-left font-semibold">HSN/SAC</th>}
                    <th className="px-2 py-2 text-right font-semibold">Qty</th>
                    <th className="px-2 py-2 text-right font-semibold">Rate</th>
                    <th className="px-2 py-2 text-right font-semibold">Disc %</th>
                    {showGst && <th className="px-2 py-2 text-right font-semibold">GST %</th>}
                    <th className="px-2 py-2 text-right font-semibold">{showGst ? "Taxable" : "Amount"}</th>
                    {showGst && <th className="px-2 py-2 text-right font-semibold">Tax</th>}
                    <th className="px-2 py-2 text-right font-semibold">Total</th>
                    <th className="px-2 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {form.lines.map((l, idx) => {
                    const p = preview?.lines?.[idx];
                    return (
                      <tr key={idx} data-testid={`editor-line-${idx}`} className="align-top">
                        <td className="px-2 py-2 space-y-1">
                          <Select value={l.item_id || ""} onValueChange={(v) => pickItem(idx, v)}>
                            <SelectTrigger className="h-8 text-xs" data-testid={`line-item-select-${idx}`}>
                              <SelectValue placeholder="Pick item" />
                            </SelectTrigger>
                            <SelectContent>
                              {items.map((it) => (
                                <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Input className="h-7 text-xs" data-testid={`line-name-${idx}`} value={l.item_name}
                            onChange={(e) => setLine(idx, { item_name: e.target.value })} placeholder="Description" />
                        </td>
                        {showGst && (
                          <td className="px-2 py-2">
                            <Input className="h-8 w-20 font-mono text-xs" data-testid={`line-hsn-${idx}`} value={l.hsn_sac_code}
                              onChange={(e) => setLine(idx, { hsn_sac_code: e.target.value })} />
                          </td>
                        )}
                        <td className="px-2 py-2">
                          <Input className="h-8 w-16 text-right font-mono text-xs" data-testid={`line-qty-${idx}`} value={l.quantity}
                            onChange={(e) => setLine(idx, { quantity: e.target.value })} />
                        </td>
                        <td className="px-2 py-2">
                          <Input className="h-8 w-24 text-right font-mono text-xs" data-testid={`line-rate-${idx}`} value={l.rate}
                            onChange={(e) => setLine(idx, { rate: e.target.value })} />
                          <RateNote line={l} />
                        </td>
                        <td className="px-2 py-2">
                          <Input className="h-8 w-16 text-right font-mono text-xs" data-testid={`line-discount-${idx}`} value={l.discount_value}
                            onChange={(e) => setLine(idx, { discount_value: e.target.value, discount_type: "PERCENT" })} />
                        </td>
                        {showGst && (
                          <td className="px-2 py-2">
                            <Select value={String(l.tax_rate_percent)} onValueChange={(v) => setLine(idx, { tax_rate_percent: Number(v) })}>
                              <SelectTrigger className="h-8 w-20 text-xs" data-testid={`line-tax-${idx}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {[0, 5, 12, 18, 28].map((r) => (
                                  <SelectItem key={r} value={String(r)}>{r}%</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                        )}
                        <td className="px-2 py-3 text-right font-mono tabular-nums" data-testid={`line-taxable-${idx}`}>
                          {num(p?.taxable_value || 0)}
                        </td>
                        {showGst && (
                          <td className="px-2 py-3 text-right font-mono tabular-nums text-slate-600">
                            {num((p?.cgst_amount || 0) + (p?.sgst_amount || 0) + (p?.igst_amount || 0) + (p?.cess_amount || 0))}
                          </td>
                        )}
                        <td className="px-2 py-3 text-right font-mono tabular-nums font-semibold" data-testid={`line-total-${idx}`}>
                          {num(p?.line_total || 0)}
                        </td>
                        <td className="px-2 py-3 text-right">
                          <button onClick={() => removeLine(idx)} data-testid={`line-remove-${idx}`}
                            className="text-slate-400 hover:text-rose-600 transition-colors duration-150">
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="divide-y divide-slate-100 lg:hidden" data-testid="editor-lines-cards">
              {form.lines.map((l, idx) => {
                const p = preview?.lines?.[idx];
                return (
                  <div key={idx} className="space-y-3 p-3.5" data-testid={`editor-line-card-${idx}`}>
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                        Line {idx + 1}
                      </span>
                      <button onClick={() => removeLine(idx)} data-testid={`line-remove-m-${idx}`}
                        className="text-slate-400 transition-colors duration-150 hover:text-rose-600">
                        <Trash2 size={15} />
                      </button>
                    </div>

                    <Select value={l.item_id || ""} onValueChange={(v) => pickItem(idx, v)}>
                      <SelectTrigger className="h-9 text-sm" data-testid={`line-item-select-m-${idx}`}>
                        <SelectValue placeholder="Pick item" />
                      </SelectTrigger>
                      <SelectContent>
                        {items.map((it) => (
                          <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Input className="h-9 text-sm" data-testid={`line-name-m-${idx}`} value={l.item_name}
                      onChange={(e) => setLine(idx, { item_name: e.target.value })} placeholder="Description" />

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-[10px] uppercase tracking-wide text-slate-500">Qty</Label>
                        <Input className="h-9 text-right font-mono text-sm" inputMode="decimal"
                          data-testid={`line-qty-m-${idx}`} value={l.quantity}
                          onChange={(e) => setLine(idx, { quantity: e.target.value })} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] uppercase tracking-wide text-slate-500">Rate</Label>
                        <Input className="h-9 text-right font-mono text-sm" inputMode="decimal"
                          data-testid={`line-rate-m-${idx}`} value={l.rate}
                          onChange={(e) => setLine(idx, { rate: e.target.value })} />
                        <RateNote line={l} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] uppercase tracking-wide text-slate-500">Discount %</Label>
                        <Input className="h-9 text-right font-mono text-sm" inputMode="decimal"
                          data-testid={`line-discount-m-${idx}`} value={l.discount_value}
                          onChange={(e) => setLine(idx, { discount_value: e.target.value, discount_type: "PERCENT" })} />
                      </div>
                      {showGst && (
                        <div className="space-y-1">
                          <Label className="text-[10px] uppercase tracking-wide text-slate-500">GST %</Label>
                          <Select value={String(l.tax_rate_percent)}
                            onValueChange={(v) => setLine(idx, { tax_rate_percent: Number(v) })}>
                            <SelectTrigger className="h-9 text-sm" data-testid={`line-tax-m-${idx}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[0, 5, 12, 18, 28].map((r) => (
                                <SelectItem key={r} value={String(r)}>{r}%</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      {showGst && (
                        <div className="col-span-2 space-y-1">
                          <Label className="text-[10px] uppercase tracking-wide text-slate-500">HSN / SAC</Label>
                          <Input className="h-9 font-mono text-sm" data-testid={`line-hsn-m-${idx}`}
                            value={l.hsn_sac_code}
                            onChange={(e) => setLine(idx, { hsn_sac_code: e.target.value })} />
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between rounded bg-slate-50 px-3 py-2 text-xs">
                      <span className="text-slate-500">
                        {showGst ? "Taxable" : "Amount"} {num(p?.taxable_value || 0)}
                        {showGst && (
                          <>
                            <span className="mx-1.5 text-slate-300">|</span>
                            Tax {num((p?.cgst_amount || 0) + (p?.sgst_amount || 0) + (p?.igst_amount || 0) + (p?.cess_amount || 0))}
                          </>
                        )}
                      </span>
                      <span className="font-mono font-semibold tabular-nums" data-testid={`line-total-m-${idx}`}>
                        {num(p?.line_total || 0)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="rounded-md border border-slate-200 bg-white p-5 grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Notes (visible to customer)</Label>
              <Textarea rows={3} data-testid="invoice-notes" value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Terms &amp; conditions</Label>
              <Textarea rows={3} data-testid="invoice-terms" value={form.terms_and_conditions}
                onChange={(e) => setForm((f) => ({ ...f, terms_and_conditions: e.target.value }))}
                placeholder="Leave blank to use your saved terms" />
            </div>
          </div>
        </div>

        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-20 space-y-3">
            <div className="rounded-md border border-slate-200 bg-white p-5" data-testid="totals-panel">
              <div className="flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Live totals</div>
                {preview?.supply_type && (
                  <span className={`rounded border px-2 py-0.5 text-[10px] font-semibold uppercase ${
                    isIntra ? "border-blue-200 bg-blue-50 text-blue-700" : "border-violet-200 bg-violet-50 text-violet-700"
                  }`} data-testid="supply-type-badge">
                    {titleCase(preview.supply_type)}
                  </span>
                )}
              </div>

              <div className="mt-4 space-y-2 text-sm">
                <Row label={showGst ? "Taxable value" : "Sub total"} value={t.taxable_value} testid="total-taxable" />
                {showGst &&
                  (isIntra ? (
                    <>
                      <Row label="CGST" value={t.cgst_amount} testid="total-cgst" />
                      <Row label={preview?.uses_utgst ? "UTGST" : "SGST"} value={t.sgst_amount} testid="total-sgst" />
                    </>
                  ) : (
                    <Row label="IGST" value={t.igst_amount} testid="total-igst" />
                  ))}
                {showGst && Number(t.cess_amount) ? <Row label="Cess" value={t.cess_amount} /> : null}
                {Number(t.tcs_amount) ? (
                  <Row label={`TCS @${Number(t.tcs_rate)}% u/s ${t.tcs_section || ""}`}
                    value={t.tcs_amount} testid="total-tcs" />
                ) : null}
                <Row label="Round off" value={t.round_off} testid="total-roundoff" />
              </div>

              <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-3">
                <span className="text-xs uppercase tracking-widest text-slate-600 font-semibold">Grand total</span>
                <span className="font-mono text-xl font-bold tabular-nums" data-testid="total-grand">{inr(t.grand_total)}</span>
              </div>
              {Number(t.tds_amount) ? (
                <div className="mt-3 space-y-2 border-t border-dashed border-slate-200 pt-3 text-sm">
                  <Row label={`Less: TDS @${Number(t.tds_rate)}% u/s ${t.tds_section || ""}`}
                    value={-Number(t.tds_amount)} testid="total-tds" />
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-600">Amount payable</span>
                    <span className="font-mono text-base font-bold tabular-nums" data-testid="total-net">
                      {inr(t.net_receivable)}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-500">
                    The invoice is still for {inr(t.grand_total)} — TDS is what your
                    customer withholds and deposits on your behalf.
                  </p>
                </div>
              ) : null}
              {preview?.amount_in_words && (
                <div className="mt-2 text-[11px] italic text-slate-500" data-testid="total-words">{preview.amount_in_words}</div>
              )}
            </div>

            {canCollect && (
              <div className="rounded-md border border-slate-200 bg-white p-5" data-testid="collect-panel">
                <label className="flex items-start justify-between gap-3">
                  <span>
                    <span className="flex items-center gap-1.5 text-sm font-medium">
                      <IndianRupee size={14} className="text-emerald-600" /> Payment received now
                    </span>
                    <span className="mt-0.5 block text-[11px] leading-snug text-slate-500">
                      Records the money against this invoice as it is issued, so a
                      counter sale is one action instead of two.
                    </span>
                  </span>
                  <Switch checked={collect} onCheckedChange={setCollect}
                    data-testid="collect-toggle" />
                </label>

                {collect && (
                  <div className="mt-4 space-y-3 border-t border-slate-100 pt-3">
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs">Amount</Label>
                        {grandTotal != null
                          && Number(collectForm.amount) !== Number(grandTotal) && (
                          <button type="button" data-testid="collect-full"
                            className="text-[11px] font-medium text-blue-700 hover:text-blue-900"
                            onClick={() => {
                              amountTouched.current = true;
                              setCollectForm((f) => ({ ...f, amount: String(grandTotal) }));
                            }}>
                            Full {inr(grandTotal)}
                          </button>
                        )}
                      </div>
                      <Input className="h-9 text-right font-mono" inputMode="decimal"
                        data-testid="collect-amount" value={collectForm.amount}
                        onChange={(e) => {
                          amountTouched.current = true;
                          setCollectForm((f) => ({ ...f, amount: e.target.value }));
                        }} />
                      {grandTotal != null && Number(collectForm.amount) > 0
                        && Number(collectForm.amount) < Number(grandTotal) && (
                        <p className="text-[11px] text-amber-700" data-testid="collect-balance">
                          {inr(Number(grandTotal) - Number(collectForm.amount))} will stay
                          outstanding on this invoice.
                        </p>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs">Mode</Label>
                      <Select value={collectForm.payment_mode}
                        onValueChange={(v) => setCollectForm((f) => ({ ...f, payment_mode: v }))}>
                        <SelectTrigger className="h-9 text-xs" data-testid="collect-mode">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PAYMENT_MODES.map((m) => (
                            <SelectItem key={m} value={m}>{titleCase(m)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {hasBanking && (
                      <div className="space-y-1.5">
                        <Label className="text-xs">Into</Label>
                        <Select value={collectForm.account_id}
                          onValueChange={(v) => setCollectForm((f) => ({ ...f, account_id: v }))}>
                          <SelectTrigger className="h-9 text-xs" data-testid="collect-account">
                            <SelectValue placeholder="Pick an account" />
                          </SelectTrigger>
                          <SelectContent>
                            {accounts.map((a) => (
                              <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {collectForm.payment_mode !== "CASH" && (
                      <div className="space-y-1.5">
                        <Label className="text-xs">
                          Reference{collectForm.payment_mode === "CHEQUE" ? " / cheque no." : ""}
                        </Label>
                        <Input className="h-9 font-mono text-xs" data-testid="collect-reference"
                          value={collectForm.reference_number}
                          onChange={(e) =>
                            setCollectForm((f) => ({ ...f, reference_number: e.target.value }))} />
                      </div>
                    )}

                    {collectForm.payment_mode === "CHEQUE" && (
                      <p className="text-[11px] text-slate-500">
                        A cheque is recorded as pending — it clears the bank balance only
                        once you mark it cleared under Payments.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="rounded-md border border-slate-200 bg-white p-5 space-y-3">
              <Label className="text-xs">Additional discount on document</Label>
              <div className="flex gap-2">
                <Select value={form.document_discount_type}
                  onValueChange={(v) => setForm((f) => ({ ...f, document_discount_type: v }))}>
                  <SelectTrigger className="h-9 w-28 text-xs" data-testid="doc-discount-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AMOUNT">₹ Amount</SelectItem>
                    <SelectItem value="PERCENT">% Percent</SelectItem>
                  </SelectContent>
                </Select>
                <Input className="h-9 text-right font-mono" data-testid="doc-discount-value"
                  value={form.document_discount_value}
                  onChange={(e) => setForm((f) => ({ ...f, document_discount_value: e.target.value }))} />
              </div>
              <div className="text-[11px] text-slate-500">
                Allocated across lines in proportion to their net amount, before tax.
              </div>
            </div>

            {isRecurring && (
              <RecurringSchedule form={form} setForm={setForm} options={recurOptions} />
            )}
            {isChallan
              ? <ChallanDetails form={form} setForm={setForm} purposes={purposes} />
              : <TaxAtSource form={form} setForm={setForm} sections={sections} />}
          </div>
        </div>
      </div>

      {/* Phones: the desktop action row is off-screen once you scroll into the
          line items, so the total and the primary actions live in a fixed bar. */}
      <div
        className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur sm:hidden"
        data-testid="mobile-action-bar"
      >
        <div className="mb-2 flex items-baseline justify-between">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
            Grand total
          </span>
          <span className="font-mono text-lg font-bold tabular-nums" data-testid="mobile-total-grand">
            {inr(t.grand_total)}
          </span>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate(-1)}
            data-testid="cancel-invoice-m" className="flex-1 gap-1.5">
            <X size={15} /> Cancel
          </Button>
          {!readOnlyIssued && !isRecurring && (
            <Button variant="outline" size="sm" disabled={busy} onClick={() => save(false)}
              data-testid="save-draft-btn-m" className="flex-1 gap-1.5">
              <Save size={15} /> Draft
            </Button>
          )}
          <Button size="sm" disabled={busy} onClick={() => save(!readOnlyIssued)}
            data-testid="issue-invoice-btn-m" className="flex-1 gap-1.5 bg-blue-600 hover:bg-blue-700">
            <Send size={15} /> {readOnlyIssued ? "Save"
              : collect && canCollect ? "Issue & collect" : "Issue"}
          </Button>
        </div>
      </div>
      {scannerOpen && (
        <Suspense fallback={null}>
          <CodeScanner open={scannerOpen} onClose={() => setScannerOpen(false)}
            onDetected={(text) => { setScannerOpen(false); submitCode(text); }} />
        </Suspense>
      )}

      {/* Keeps the last card clear of the fixed bar. */}
      <div className="h-28 sm:hidden" />
    </div>
  );
}

const Row = ({ label, value, testid }) => (
  <div className="flex items-center justify-between">
    <span className="text-slate-600 text-xs">{label}</span>
    <span className="font-mono tabular-nums" data-testid={testid}>{inr(value)}</span>
  </div>
);
